// The part of a live-systems result that belongs to the PLATFORM, not the service.
//
//   NAME                       READY   STATUS    RESTARTS      AGE
//   skwach-6db6ddc76d-d2wzp    1/1     Running   3 (2d ago)    5d
//
// Kubernetes defines that table. It is identical on every cluster and every tenant,
// so parsing it needs no knowledge of what skwach does — which is exactly the line
// that makes state reconstruction safe here. On the same corpus, 51 of 92 inspect
// results came back in this schema or as `-o json`/jsonpath.
//
// What is deliberately NOT parsed: log lines, and probe output. `partition 29:
// low=4139 high=8264` means something only to someone who knows Kafka and knows
// this service, and a parser for it would silently mis-report the day the service
// changes a print statement. That content travels as evidence, uninterpreted.

const HEADER_RE = /^\s*NAME\s+(.*)$/;
const READY_RE = /^(\d+)\/(\d+)$/;

/**
 * Rows of a kubectl table, as {column: value}. Splits on runs of 2+ spaces so a
 * value containing a single space — `3 (2d ago)`, `Init:0/1` — stays intact.
 */
export function parseKubectlTable(text) {
  const lines = String(text || "").split("\n");
  const at = lines.findIndex((l) => HEADER_RE.test(l));
  if (at === -1) return [];
  const cols = lines[at].trim().split(/\s{2,}/).map((c) => c.trim().toLowerCase());
  const rows = [];
  for (const line of lines.slice(at + 1)) {
    // A SECOND header ends this table. One command routinely runs `get pods` then
    // `get pvc`, and skipping the header while continuing to read rows folded the
    // second table's columns into the first — volume ids landing in `status` and a
    // `500Gi` capacity read as 500 restarts.
    if (HEADER_RE.test(line)) break;
    if (!line.trim()) continue;
    const cells = line.trim().split(/\s{2,}/);
    // A wrapped or truncated line is not a row. Dropping it beats emitting a row
    // whose columns are shifted by one.
    if (cells.length < 2 || cells.length > cols.length + 1) continue;
    const row = {};
    cols.forEach((c, i) => { if (cells[i] != null) row[c] = cells[i].trim(); });
    if (row.name) rows.push(row);
  }
  return rows;
}

/**
 * Platform facts about one service, folded from the rows belonging to it.
 *
 * Aggregated across pods on purpose: the service is the unit a reader recognises,
 * and "2/3 ready" is the fact worth having, not three separate pod readings.
 */
export function readServiceState(op, serviceOf) {
  if (!op?.output || op.kind !== "inspect") return null;
  const rows = parseKubectlTable(op.output);
  if (!rows.length) return null;
  // ONLY a pod table. Every resource type has its own columns, and folding a `get
  // pvc` table into pod state put volume ids into `status` and a `500Gi` capacity
  // into `restarts` as the number 500. Sharing a NAME column is not sharing a
  // schema, and merging two schemas is worse than reading neither.
  if (!("ready" in rows[0]) || !("restarts" in rows[0])) return null;

  const mine = op.service ? rows.filter((r) => serviceOf(r.name) === op.service) : rows;
  if (!mine.length) return null;

  let ready = 0;
  let desired = 0;
  let readySeen = false;
  let restarts = 0;
  let restartsSeen = false;
  const statuses = new Map();

  for (const r of mine) {
    const m = READY_RE.exec(r.ready || "");
    if (m) { ready += Number(m[1]); desired += Number(m[2]); readySeen = true; }
    if (r.restarts != null) {
      // `3 (2d ago)` — the count is the leading integer; the parenthetical is when
      // the last one happened and is not part of the number.
      const n = /^(\d+)/.exec(r.restarts);
      if (n) { restarts += Number(n[1]); restartsSeen = true; }
    }
    if (r.status) statuses.set(r.status, (statuses.get(r.status) || 0) + 1);
  }

  const status = [...statuses.entries()].sort((a, b) => b[1] - a[1])
    .map(([s, n]) => (statuses.size > 1 ? `${n} ${s}` : s)).join(", ") || null;

  const state = {
    pods: mine.length,
    ready: readySeen ? `${ready}/${desired}` : null,
    status,
    restarts: restartsSeen ? restarts : null,
  };
  return Object.values(state).some((v) => v != null) ? state : null;
}

/** A mutation this op made, in the platform's vocabulary. */
export function readServiceChange(op) {
  if (op?.kind !== "change") return null;
  const target = op.service || op.namespace || "the cluster";
  return `${op.verb} ${op.resource || ""}`.trim() + (op.service ? "" : ` in ${target}`);
}
